import {ApexOptions} from 'apexcharts'
import {getCardColor} from './utils'
import {CHART_COLORS, CHART_COLOR_PALETTES} from './colors'

/**
 * Get base chart options for line/area charts
 */
export const getBaseChartOptions = (color: string, noDataText: string): ApexOptions => {
  return {
    chart: {
      type: 'area',
      stacked: false,
      height: 350,
      toolbar: {
        show: false,
        autoSelected: 'zoom',
      },
    },
    noData: {
      text: noDataText,
      offsetY: -20,
      style: {
        fontSize: '20px',
        color: CHART_COLORS.NO_DATA_TEXT,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 5,
      },
    },
    dataLabels: {
      enabled: false,
    },
    yaxis: {
      labels: {
        formatter: function (val: any) {
          return val?.toFixed(0)
        },
      },
      title: {
        text: '',
      },
    },
    xaxis: {
      type: 'datetime',
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        inverseColors: false,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 90, 100],
      },
    },
    stroke: {
      width: 4,
      curve: 'smooth',
    },
    colors: [color],
  }
}

/**
 * Get total scan chart options
 */
export const getTotalScanChartOptions = (noDataText: string): ApexOptions => {
  return {
    chart: {
      type: 'area',
      stacked: false,
      height: 350,
      zoom: {
        type: 'x',
        enabled: false,
        autoScaleYaxis: true,
      },
      toolbar: {
        show: false,
        autoSelected: 'zoom',
      },
    },
    legend: {
      show: false,
    },
    colors: [...CHART_COLOR_PALETTES.COMPARISON_TWO],
    noData: {
      text: noDataText,
      align: 'center',
      verticalAlign: 'middle',
      style: {
        fontSize: '20px',
        color: CHART_COLORS.NO_DATA_TEXT,
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
    },
    title: {
      text: '',
      align: 'left',
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        inverseColors: false,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 90, 100],
      },
    },
    yaxis: {
      labels: {
        formatter: function (val: any) {
          return val?.toFixed(0)
        },
      },
      title: {
        text: '',
      },
    },
    xaxis: {
      type: 'datetime',
    },
    tooltip: {
      shared: true,
      y: {
        formatter: function (val: any) {
          return val?.toFixed(0)
        },
      },
      custom: function ({series, seriesIndex, dataPointIndex, w}) {
        const names = w.globals.seriesNames
        const colors = CHART_COLOR_PALETTES.COMPARISON_TWO
        let html = '<div style="padding:4px 8px">'
        for (let i = 0; i < series.length; i++) {
          if (typeof series[i][dataPointIndex] !== 'undefined') {
            html += `<div style="display:flex;align-items:center;margin-bottom:2px;">
              <span style="display:inline-block;width:12px;height:6px;background:${colors[i]};border-radius:2px;margin-right:8px;"></span>
              <span style="font-weight:500;">${names[i]}</span>
              <span style="margin-left:8px;">${series[i][dataPointIndex]}</span>
            </div>`
          }
        }
        html += '</div>'
        return html
      },
    },
  }
}

/**
 * Get stacked bar chart options
 */
export const getStackedBarChartOptions = (
  cardList: string[],
  categories: string[],
  noDataText: string,
  quantityUnit: string
): ApexOptions => {
  return {
    chart: {
      type: 'bar',
      height: 400,
      stacked: true,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%',
        borderRadius: 0,
      },
    },
    // Generate colors from our cards - ensure we have valid colors using project theme
    colors:
      cardList.length > 0
        ? cardList.map((card) => getCardColor(card))
        : [...CHART_COLOR_PALETTES.DEFAULT],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 1,
      colors: ['transparent'],
    },
    xaxis: {
      categories: Array.isArray(categories) ? categories.map((cat) => String(cat)) : [],
      title: {
        text: '',
      },
      labels: {
        style: {
          colors: CHART_COLORS.TEXT_MUTED,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      title: {
        text: '',
      },
      labels: {
        style: {
          colors: CHART_COLORS.TEXT_MUTED,
          fontSize: '12px',
        },
        formatter: function (val: any) {
          return val?.toFixed(0)
        },
      },
    },
    fill: {
      opacity: 0.8,
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      offsetY: 20,
      markers: {
        width: 12,
        height: 12,
        radius: 2,
      },
      itemMargin: {
        horizontal: 15,
        vertical: 5,
      },
      fontSize: '13px',
      fontWeight: 500,
    },
    tooltip: {
      y: {
        formatter: function (val: any) {
          return val + ' ' + quantityUnit
        },
      },
      shared: true,
      intersect: false,
    },
    grid: {
      borderColor: CHART_COLORS.GRID,
      strokeDashArray: 3,
    },
    noData: {
      text: noDataText,
      align: 'center',
      verticalAlign: 'middle',
      style: {
        fontSize: '18px',
        color: CHART_COLORS.TEXT_MUTED,
      },
    },
  }
}

/**
 * Get card comparison chart options
 */
export const getCardComparisonOptions = (categories: string[]): ApexOptions => {
  return {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
      },
    },
    colors: [...CHART_COLOR_PALETTES.PROMOTE_ACTIONS],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 1,
      colors: ['transparent'],
    },
    grid: {
      show: true,
      borderColor: CHART_COLORS.GRID,
      strokeDashArray: 3,
      row: {
        colors: ['transparent'],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: Array.isArray(categories) ? categories.map((cat) => String(cat)) : [],
      title: {
        text: '',
      },
      labels: {
        style: {
          colors: CHART_COLORS.TEXT_MUTED,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      title: {
        text: '',
      },
      min: 0,
      labels: {
        style: {
          colors: CHART_COLORS.TEXT_MUTED,
          fontSize: '12px',
        },
        formatter: function (val: number) {
          return val.toFixed(0)
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val: number) {
          return val.toFixed(0)
        },
      },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      offsetX: 0,
      offsetY: 20,
    },
  }
}
